import { useEffect, useRef } from 'react';
import type {
  V2Balance,
  V2CardDetailResponse,
  V2Document,
  V2HistoryRecord,
  V2Request,
  V2Token,
  V2XStock,
} from '../types/v2';
import type { CardBlockMode } from '../screens/Card/CardBlockScreen';
import type { ExchangePreset } from '../screens/Account/Exchange/ExchangeScreen';
import type { OperationsTab } from '../screens/Operations/OperationsScreen';

/**
 * What the app holds about the signed-in user's own money between screens:
 * the balance, card, deposit or request picked, a scanned address, a preset
 * order. All of it belongs to one user, and it all goes when the user does —
 * on sign-out, and when another user takes over (another login, or the start
 * or end of "Login as user"). App keeps each field in its own useState, seeded
 * from INITIAL_USER_SCOPED_STATE, and hands resetUserScopedState a setter for
 * every one — a field added here without a setter there does not compile.
 */
export interface UserScopedState {
  selectedBalance: V2Balance | null;
  selectedInvestmentAccountId: string | null;
  selectedTFundAccountId: string | null;
  selectedCardBalance: V2Balance | null;
  cardBlockMode: CardBlockMode;
  cardDetail: V2CardDetailResponse | null;
  cardDetailLoading: boolean;
  selectedTransaction: any;
  selectedHistoryRecord: V2HistoryRecord | null;
  selectedRequest: V2Request | null;
  selectedMarketToken: V2Token | null;
  selectedMarketStock: V2XStock | null;
  exchangePreset: ExchangePreset | null;
  repeatRequest: V2Request | null;
  withdrawNetworkId: string | null;
  operationsInitialTab: OperationsTab;
  pdfDocument: V2Document | null;
  scannedAddress: string | null;
}

export const INITIAL_USER_SCOPED_STATE: UserScopedState = {
  selectedBalance: null,
  selectedInvestmentAccountId: null,
  selectedTFundAccountId: null,
  selectedCardBalance: null,
  cardBlockMode: 'block',
  cardDetail: null,
  cardDetailLoading: false,
  selectedTransaction: null,
  selectedHistoryRecord: null,
  selectedRequest: null,
  selectedMarketToken: null,
  selectedMarketStock: null,
  exchangePreset: null,
  repeatRequest: null,
  withdrawNetworkId: null,
  operationsInitialTab: 'inflow',
  pdfDocument: null,
  scannedAddress: null,
};

/** A setter for every field — a missing one is a type error, so no field can be left behind. */
export type UserScopedSetters = {
  [K in keyof UserScopedState]: (value: UserScopedState[K]) => void;
};

/** Puts every user-scoped field back to its initial value. */
export const resetUserScopedState = (setters: UserScopedSetters): void => {
  (Object.keys(INITIAL_USER_SCOPED_STATE) as (keyof UserScopedState)[]).forEach((key) => {
    (setters[key] as (value: unknown) => void)(INITIAL_USER_SCOPED_STATE[key]);
  });
};

/**
 * Calls `onUserGone` when the signed-in user goes: on sign-out (an id becomes
 * null), and when another user takes over without one (one id becomes
 * another — another login, the start or end of "Login as user"). Signing in
 * from nothing is not a change of user, so the first user's state stands.
 */
export const useOnUserGone = (userId: string | null, onUserGone: () => void): void => {
  const previous = useRef<string | null>(userId);
  useEffect(() => {
    if (previous.current === userId) {
      return;
    }
    const was = previous.current;
    previous.current = userId;
    if (was !== null) {
      onUserGone();
    }
  }, [userId, onUserGone]);
};
